import { defineStruct, GPUType } from "@engine/render2d";

// ============================================================================
// PREDEFINED VFX LAYOUTS
// ============================================================================



/**
 * VFX Particle struct layout.
 * Matches the struct generated by generateParticleStruct().
 */
export const PARTICLE_LAYOUT = defineStruct('Particle', {
  position: GPUType.f32().vec3(),
  lifetime: GPUType.f32().build(),
  maxLifetime: GPUType.f32().build(),
  velocity: GPUType.f32().vec3(),
  color: GPUType.f32().vec4(),
  size: GPUType.f32().build(),
  scale: GPUType.f32().vec2(),
  rotation: GPUType.f32().build(),
  angularVelocity: GPUType.f32().build(),
  spriteFrame: GPUType.f32().build(),
  seed: GPUType.u32().build(),
  alive: GPUType.u32().build(),
});

/**
 * VFX Counters struct layout.
 */
export const COUNTERS_LAYOUT = defineStruct('Counters', {
  aliveCount: GPUType.u32().atomic(),
  deadCount: GPUType.u32().atomic(),
  emitCount: GPUType.u32().atomic(),
  _unused: GPUType.u32().build(),
  spawnAccumulator: GPUType.u32().atomic(),
  spawnOffset: GPUType.u32().atomic(),
});

/**
 * VFX Uniforms struct layout.
 */
export const UNIFORMS_LAYOUT = defineStruct('Uniforms', {
  time: GPUType.f32().build(),
  deltaTime: GPUType.f32().build(),
  particleCount: GPUType.u32().build(),
  maxParticles: GPUType.u32().build(),
  spawnOffset: GPUType.u32().build(),
  emitterX: GPUType.f32().build(),
  emitterY: GPUType.f32().build(),
  spawnRate: GPUType.f32().build(),
  emitterScaleX: GPUType.f32().build(),
  emitterScaleY: GPUType.f32().build(),
  viewMatrix: GPUType.f32().mat4x4(),
  projMatrix: GPUType.f32().mat4x4(),
  // Emitter velocity (world units/sec, from position deltas) — feeds the
  // `emitterVelocity` env builtin (Inherit Velocity node). Appended AFTER the
  // matrices so all pre-existing field offsets stay unchanged.
  emitterVelX: GPUType.f32().build(),
  emitterVelY: GPUType.f32().build(),
});

/**
 * GPU Event struct layout.
 */
export const GPU_EVENT_LAYOUT = defineStruct('GPUEvent', {
  position: GPUType.f32().vec3(),
  spawnCount: GPUType.u32().build(),
  velocity: GPUType.f32().vec3(),
  eventId: GPUType.u32().build(),
  color: GPUType.f32().vec4(),
});

/**
 * Render uniforms layout (for vertex/fragment shaders).
 * Simpler than compute uniforms - just viewProjection + time.
 */
export const RENDER_UNIFORMS_LAYOUT = defineStruct('VFXUniforms', {
  viewProjection: GPUType.f32().mat4x4(),
  time: GPUType.f32().build(),
  emitterX: GPUType.f32().build(),
  emitterY: GPUType.f32().build(),
  emitterScaleX: GPUType.f32().build(),
  emitterScaleY: GPUType.f32().build(),
});

/**
 * VFX Variables struct layout.
 * Contains runtime-configurable parameters for VFX systems.
 */
export const VFX_VARIABLES_LAYOUT = defineStruct('VFXVariables', {
  // 16 float variables packed as 4 vec4s (64 bytes)
  floats0: GPUType.f32().vec4(),
  floats1: GPUType.f32().vec4(),
  floats2: GPUType.f32().vec4(),
  floats3: GPUType.f32().vec4(),
  // 8 vec2 variables packed as 4 vec4s (64 bytes)
  vec2s0: GPUType.f32().vec4(),
  vec2s1: GPUType.f32().vec4(),
  vec2s2: GPUType.f32().vec4(),
  vec2s3: GPUType.f32().vec4(),
  // 8 vec3 variables as vec4s for alignment (128 bytes)
  vec3_0: GPUType.f32().vec4(),
  vec3_1: GPUType.f32().vec4(),
  vec3_2: GPUType.f32().vec4(),
  vec3_3: GPUType.f32().vec4(),
  vec3_4: GPUType.f32().vec4(),
  vec3_5: GPUType.f32().vec4(),
  vec3_6: GPUType.f32().vec4(),
  vec3_7: GPUType.f32().vec4(),
  // 8 color variables (128 bytes)
  color0: GPUType.f32().vec4(),
  color1: GPUType.f32().vec4(),
  color2: GPUType.f32().vec4(),
  color3: GPUType.f32().vec4(),
  color4: GPUType.f32().vec4(),
  color5: GPUType.f32().vec4(),
  color6: GPUType.f32().vec4(),
  color7: GPUType.f32().vec4(),
});

/**
 * Event count struct layout (shared between systems).
 */
export const EVENT_COUNT_LAYOUT = defineStruct('EventCount', {
  count: GPUType.u32().atomic(),
});

/**
 * Spawn shader uniforms layout (simplified version without matrices).
 */
export const SPAWN_UNIFORMS_LAYOUT = defineStruct('SpawnUniforms', {
  time: GPUType.f32().build(),
  deltaTime: GPUType.f32().build(),
  particleCount: GPUType.u32().build(),
  maxParticles: GPUType.u32().build(),
  _unused: GPUType.u32().build(),
  emitterX: GPUType.f32().build(),
  emitterY: GPUType.f32().build(),
  spawnRate: GPUType.f32().build(),
  emitterScaleX: GPUType.f32().build(),
  emitterScaleY: GPUType.f32().build(),
});

/**
 * VFX render uniforms layout.
 */
export const VFX_RENDER_UNIFORMS_LAYOUT = defineStruct('VFXUniforms', {
  viewProjection: GPUType.f32().mat4x4(),
  time: GPUType.f32().build(),
  emitterX: GPUType.f32().build(),
  emitterY: GPUType.f32().build(),
  emitterScaleX: GPUType.f32().build(),
  emitterScaleY: GPUType.f32().build(),
});

// ============================================================================
// TYPESCRIPT INTERFACES FOR STRUCT TYPES
// ============================================================================

/** TypeScript interface matching Uniforms struct */
export interface UniformsData {
  time: number;
  deltaTime: number;
  particleCount: number;
  maxParticles: number;
  spawnOffset: number;
  emitterX: number;
  emitterY: number;
  spawnRate: number;
  emitterScaleX: number;
  emitterScaleY: number;
  viewMatrix: Float32Array | number[];
  projMatrix: Float32Array | number[];
  emitterVelX: number;
  emitterVelY: number;
}

/** TypeScript interface matching GPUEvent struct */
export interface GPUEventData {
  position: { x: number; y: number; z: number };
  spawnCount: number;
  velocity: { x: number; y: number; z: number };
  eventId: number;
  color: { x: number; y: number; z: number; w: number };
}

/** TypeScript interface matching Counters struct */
export interface CountersData {
  aliveCount: number;
  deadCount: number;
  emitCount: number;
  _unused: number;
  spawnAccumulator: number;
  spawnOffset: number;
}

/** TypeScript interface matching Particle struct */
export interface ParticleData {
  position: { x: number; y: number; z: number };
  lifetime: number;
  maxLifetime: number;
  velocity: { x: number; y: number; z: number };
  color: { x: number; y: number; z: number; w: number };
  size: number;
  scale: { x: number; y: number };
  rotation: number;
  angularVelocity: number;
  spriteFrame: number;
  seed: number;
  alive: number;
}

/**
 * Vertex input layout for particle rendering.
 */
export const VERTEX_INPUT_LOCATIONS = {
  particlePos: 0,
  velocity: 1,
  color: 2,
  size: 3,
  scale: 4,
  rotation: 5,
  lifetime: 6,
  maxLifetime: 7,
  alive: 8,
} as const;
